// src/components/communications/CommunicationsHub.jsx
// State-of-the-art Communications Hub - unified inbox for SMS, Email, Calls
import React, { useState, useEffect, useCallback } from 'react';
import { X, MessageSquare, Mail, Phone, Search, Filter, ChevronRight, Clock, CheckCheck, AlertCircle, ClipboardCheck } from 'lucide-react';
import SMSThread from './SMSThread';
import EmailThread from './EmailThread';
import CallLog from './CallLog';
import SMSOutcomeModal from '../SMSOutcomeModal';

const API_BASE = process.env.REACT_APP_API_URL || '';

export default function CommunicationsHub({ clientId, clientName, clientPhone, onClose }) {
  const [activeTab, setActiveTab] = useState('sms');
  const [communications, setCommunications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({ sms: 0, email: 0, call: 0 });
  const [showSMSOutcome, setShowSMSOutcome] = useState(false);

  // Fetch all communications for client
  const fetchCommunications = useCallback(async () => {
    if (!clientId) return;

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/clients/${clientId}/comms/all`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setCommunications(data.data || data || []);

        // Calculate unread counts
        const counts = { sms: 0, email: 0, call: 0 };
        (data.data || data || []).forEach(c => {
          if (!c.is_read && c.direction === 'inbound') {
            counts[c.type] = (counts[c.type] || 0) + 1;
          }
        });
        setUnreadCounts(counts);
      }
    } catch (err) {
      console.error('Failed to fetch communications:', err);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchCommunications();
  }, [fetchCommunications]);

  // Filter communications by type and search
  const filteredComms = communications.filter(c => {
    if (c.type !== activeTab) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const body = (c.body || '').toLowerCase();
      const subject = (c.subject || '').toLowerCase();
      return body.includes(query) || subject.includes(query);
    }
    return true;
  });

  // Group SMS into conversation thread
  const smsThread = communications
    .filter(c => c.type === 'sms')
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

  // Group emails by thread/subject
  const emailThread = communications
    .filter(c => c.type === 'email')
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  // Get calls with recordings
  const callLog = communications
    .filter(c => c.type === 'call')
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const tabs = [
    { id: 'sms', label: 'Messages', icon: MessageSquare, count: unreadCounts.sms },
    { id: 'email', label: 'Email', icon: Mail, count: unreadCounts.email },
    { id: 'call', label: 'Calls', icon: Phone, count: unreadCounts.call }
  ];

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Main Panel */}
      <div className="relative ml-auto w-full sm:max-w-4xl h-full bg-white shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gradient-to-r from-slate-900 to-slate-800">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-amber-500 flex items-center justify-center text-white font-bold text-lg">
              {clientName?.charAt(0) || '?'}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{clientName || 'Client'}</h2>
              <p className="text-sm text-gray-400">{clientPhone || 'No phone'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 bg-gray-50">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-4 font-medium transition-all relative ${
                activeTab === tab.id
                  ? 'text-amber-600 border-b-2 border-amber-500 bg-white'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span>{tab.label}</span>
              {tab.count > 0 && (
                <span className="absolute top-2 right-1/4 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <>
              {activeTab === 'sms' && (
                <>
                  {/* SMS Thread - takes all remaining space */}
                  <div className="flex-1 min-h-0">
                    <SMSThread
                      messages={smsThread}
                      clientId={clientId}
                      clientName={clientName}
                      clientPhone={clientPhone}
                      onMessageSent={fetchCommunications}
                    />
                  </div>
                  {/* Log Outcome Button - Always visible at bottom */}
                  {smsThread.length > 0 && (
                    <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 flex-shrink-0">
                      <button
                        onClick={() => setShowSMSOutcome(true)}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                      >
                        <ClipboardCheck className="w-5 h-5" />
                        Log Conversation Outcome
                      </button>
                    </div>
                  )}
                </>
              )}
              {activeTab === 'email' && (
                <EmailThread
                  emails={emailThread}
                  clientId={clientId}
                  clientName={clientName}
                  onEmailSent={fetchCommunications}
                />
              )}
              {activeTab === 'call' && (
                <CallLog
                  calls={callLog}
                  clientId={clientId}
                  clientName={clientName}
                  clientPhone={clientPhone}
                  onRefresh={fetchCommunications}
                />
              )}
            </>
          )}
        </div>
      </div>

      {/* SMS Outcome Modal */}
      <SMSOutcomeModal
        isOpen={showSMSOutcome}
        onClose={() => setShowSMSOutcome(false)}
        client={{ id: clientId, firstName: clientName?.split(' ')[0], lastName: clientName?.split(' ').slice(1).join(' ') }}
        onSave={(result) => {
          // Dispatch event to refresh risk score card
          window.dispatchEvent(new CustomEvent('risk-score-updated', { detail: { clientId } }));
          setShowSMSOutcome(false);
        }}
      />
    </div>
  );
}
